'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Switch, TextField, InputAdornment } from '@mui/material';
import { Additem } from 'iconsax-react';
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../application/store/useStore';
import { useEffect, useState } from 'react';
import { deleteCompanyAsync } from '../../../application/features/company/companyThunk';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import { NewCompanyDialog } from './new-company-dilaog';
import { toast } from 'react-toastify';
import { DeleteDialog } from './delete-company-dialog';
import { CustomGrid } from '../../components/common/dataGrid';
import { getCompaniesAsync, toggleCompanyActivationAsync } from '../../../application/features/company/companyThunk';
import { EditCompanyDialog } from './edit-company-dialog';
const Companies = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { allCompanies, company } = useAppSelector((state) => state.company);
    const currentUser = useAppSelector((state) => state.auth.currentUser);
    const [selectedCompanyName, setSelectedCompanyName] = useState(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [newCompanyDialog, setNewCompanyDialog] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [companyToEdit, setCompanyToEdit] = useState(undefined);
    const [searchTerm, setSearchTerm] = useState('');
    const dispatch = useAppDispatch();
    const [sortModel, setSortModel] = useState([
        { field: 'createdDate', sort: 'desc' }
    ]);
    const openDeleteDialog = (companyId, companyName) => {
        setSelectedCompanyId(companyId);
        setSelectedCompanyName(companyName);
        setIsDeleteDialogOpen(true);
    };
    const closeDeleteDialog = () => {
        setSelectedCompanyId(null);
        setSelectedCompanyName(null);
        setIsDeleteDialogOpen(false);
    };
    const closeNewCompanyDialog = () => {
        setNewCompanyDialog(false);
    };
    const openEditDialog = (company) => {
        setCompanyToEdit(company);
        setIsEditDialogOpen(true);
    };
    const closeEditDialog = () => {
        setCompanyToEdit(undefined);
        setIsEditDialogOpen(false);
    };
    const handleEditCompany = () => {
        dispatch(getCompaniesAsync());
    };
    const toggleActivation = (id) => {
        dispatch(toggleCompanyActivationAsync(id)).then(() => {
            dispatch(getCompaniesAsync());
        });
    };
    const columns = [
        {
            field: 'logo',
            headerName: t('logo'),
            flex: 1,
            minWidth: 64,
            renderCell: (params) => (_jsx(Box, { display: 'flex', alignItems: 'center', height: '100%', children: params.value ? (_jsx("img", { src: params.value, crossOrigin: "anonymous", alt: 'Company logo', style: {
                        width: '48px',
                        height: '48px',
                        objectFit: 'contain',
                        borderRadius: '50%'
                    } })) : (_jsx(Typography, { variant: 'body2', children: "N/A" })) }))
        },
        {
            field: 'name',
            headerName: t('name'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Box, { display: 'flex', alignItems: 'center', height: '100%', children: _jsx(Typography, { variant: 'body2', children: params.value || 'N/A' }) }))
        },
        {
            field: 'address',
            headerName: t('address'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Box, { display: 'flex', alignItems: 'center', height: '100%', children: _jsx(Typography, { variant: 'body2', children: params.value || 'N/A' }) }))
        },
        {
            field: 'companyDomain',
            headerName: t('domain'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Box, { display: 'flex', alignItems: 'center', height: '100%', children: params.value ? (_jsx(Box, { component: 'a', href: params.value.startsWith('http') ? params.value : `https://${params.value}`, target: '_blank', rel: 'noopener noreferrer', sx: {
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                    }, children: params.value })) : (_jsx(Typography, { variant: 'body2', children: "N/A" })) }))
        },
        {
            field: 'email',
            headerName: t('email'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Box, { display: 'flex', alignItems: 'center', height: '100%', children: params.value ? (_jsx(Box, { component: 'a', href: `mailto:${params.value}`, sx: {
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                    }, children: params.value })) : (_jsx(Typography, { variant: 'body2', children: "N/A" })) }))
        },
        {
            field: 'phoneNumber',
            headerName: t('phoneNumber'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Box, { display: 'flex', alignItems: 'center', height: '100%', children: _jsx(Typography, { variant: 'body2', children: params.value || 'N/A' }) }))
        },
        {
            field: 'description',
            headerName: t('description'),
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (_jsx(Box, { display: 'flex', alignItems: 'center', height: '100%', children: _jsx(Typography, { variant: 'body2', children: params.value || 'N/A' }) }))
        },
        {
            field: 'isActive',
            headerName: t('isActive'),
            flex: 1,
            minWidth: 80,
            renderCell: (params) => (_jsx(Switch, { checked: params.row.isActive, onChange: () => toggleActivation(params.row.id) }))
        },
        {
            field: 'actions',
            headerName: t('actions'),
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (_jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(IconButton, { color: 'primary', onClick: () => openEditDialog(params.row), children: _jsx(EditIcon, {}) }), _jsx(IconButton, { color: 'error', onClick: () => openDeleteDialog(params.row.id, params.row.name), children: _jsx(DeleteIcon, {}) })] }))
        }
    ];
    useEffect(() => {
        dispatch(getCompaniesAsync());
    }, [dispatch, company]);
    const handleDelete = () => {
        if (selectedCompanyId) {
            dispatch(deleteCompanyAsync(selectedCompanyId))
                .unwrap()
                .then(() => {
                toast.success('Company has been deleted.');
                closeDeleteDialog();
                dispatch(getCompaniesAsync());
            })
                .catch(() => {
                toast.error('Failed to delete company.');
            });
        }
    };
    // Filter companies based on search
    const filteredCompanies = (allCompanies || []).filter((company) => {
        if (!searchTerm.trim())
            return true;
        const searchLower = searchTerm.toLowerCase();
        return (company.name?.toLowerCase().includes(searchLower) ||
            company.email?.toLowerCase().includes(searchLower) ||
            company.address?.toLowerCase().includes(searchLower) ||
            company.domain?.toLowerCase().includes(searchLower) ||
            company.telephone?.toLowerCase().includes(searchLower) ||
            company.description?.toLowerCase().includes(searchLower));
    });
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, paddingY: 1, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, children: [_jsx(Typography, { variant: 'h3', children: t('companies') }), currentUser?.role === 'SuperAdmin' && (_jsx(Button, { variant: 'contained', startIcon: _jsx(Additem, {}), color: 'primary', size: 'large', onClick: () => setNewCompanyDialog(true), children: t('newCompany') }))] }), _jsx(Box, { display: 'flex', justifyContent: 'flex-start', children: _jsx(TextField, { placeholder: t('searchCompanies') ||
                                'Search by name, email, domain, address, phone, or description...', value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), sx: { width: { xs: '100%', sm: 400, md: 450 }, maxWidth: '100%' }, size: 'small', InputProps: {
                                startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(SearchIcon, {}) }))
                            } }) }), searchTerm.trim() && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: filteredCompanies.length > 0
                            ? `Found ${filteredCompanies.length} ${filteredCompanies.length === 1 ? 'company' : 'companies'} matching "${searchTerm}"`
                            : `No companies found matching "${searchTerm}"` }))] }), _jsx(Box, { width: '100%', mt: 1, sx: { height: 'calc(100vh - 230px)' }, children: _jsx(CustomGrid, { rows: filteredCompanies, columns: columns, sortModel: sortModel, onSortModelChange: setSortModel }) }), _jsx(NewCompanyDialog, { openCompanyDialog: newCompanyDialog, onClose: closeNewCompanyDialog }), _jsx(DeleteDialog, { open: isDeleteDialogOpen, proposalName: selectedCompanyName ?? '', onClose: closeDeleteDialog, onDelete: handleDelete }), _jsx(EditCompanyDialog, { openEditCompanyDialog: isEditDialogOpen, onCloseEditCompany: closeEditDialog, companyToEdit: companyToEdit, onEditCompany: handleEditCompany })] }));
};
export default Companies;
